import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Dice5, Copy, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "../../components/ui/dialog";
import { generatePassword, fromDateInput } from "./utils";
import TierPreview from "./TierPreview";

export default function AddFirmDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(getInitial());
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [copied, setCopied] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(false);

  function getInitial() {
    const start = new Date();
    const end = new Date(); end.setFullYear(end.getFullYear() + 1);
    return {
      company_name: "",
      admin_name: "",
      admin_email: "",
      admin_password: "",
      // Filled in from the fetched tier list once loaded.
      plan_tier: "",
      subscription_start_date: start.toISOString().slice(0, 10),
      subscription_end_date: end.toISOString().slice(0, 10),
    };
  }

  const loadTiers = useCallback(async () => {
    setTiersLoading(true);
    try {
      const { data } = await api.get("/superadmin/subscription-tiers");
      const active = (data.tiers || []).filter((t) => !t.archived);
      setTiers(active);
      // Default to the first non-Trial active tier so new firms start on a
      // paid plan; fall back to Trial or the very first tier if nothing else
      // exists.
      setF((s) => {
        if (s.plan_tier) return s;
        const preferred = active.find((t) => t.slug === "essential")
          || active.find((t) => t.slug !== "trial")
          || active[0];
        return preferred ? { ...s, plan_tier: preferred.slug } : s;
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTiersLoading(false);
    }
  }, []);

  // Refresh the tier list every time the dialog opens so the super admin
  // sees any newly-created / edited tiers from Subscription tiers tab.
  useEffect(() => {
    if (open) loadTiers();
  }, [open, loadTiers]);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const genPw = () => upd("admin_password", generatePassword(12));

  const submit = async (e) => {
    e.preventDefault();
    if (f.admin_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (!f.plan_tier) { toast.error("Pick a subscription tier"); return; }
    setLoading(true);
    try {
      await api.post("/superadmin/workspaces", {
        company_name: f.company_name,
        admin_name: f.admin_name,
        admin_email: f.admin_email.trim().toLowerCase(),
        admin_password: f.admin_password,
        plan_tier: f.plan_tier,
        subscription_start_date: fromDateInput(f.subscription_start_date),
        subscription_end_date: fromDateInput(f.subscription_end_date),
      });
      setDone({ email: f.admin_email.trim().toLowerCase(), password: f.admin_password, name: f.company_name });
      toast.success("ID firm created");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const close = () => { setOpen(false); setDone(null); setF(getInitial()); setCopied(false); };

  const copyCreds = async () => {
    const text = `OrkaiFlow — sign-in credentials for ${done.name}\nEmail: ${done.email}\nPassword: ${done.password}`;
    try { await navigator.clipboard.writeText(text); setCopied(true); toast.success("Copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copy failed"); }
  };

  const selectedTier = tiers.find((t) => t.slug === f.plan_tier) || null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else setOpen(true); }}>
      <DialogTrigger asChild>
        <button data-testid="sa-add-firm-btn" className="btn-primary inline-flex items-center gap-2">
          <Plus size={16} /> Add ID firm
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Onboard a new ID firm</DialogTitle>
          <DialogDescription>Creates a workspace, subscription and its first admin. Share the credentials with the firm.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="space-y-4">
            <div className="card-soft !bg-[#F7F4EC] !p-5">
              <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2">Share with {done.name}</div>
              <div className="text-xs text-[#7A7571]">Sign-in email</div>
              <div className="font-mono text-sm text-[#2C2A29] mb-3 break-all">{done.email}</div>
              <div className="text-xs text-[#7A7571]">Password</div>
              <div className="font-mono text-sm text-[#2C2A29]">{done.password}</div>
              <button onClick={copyCreds} data-testid="sa-add-firm-copy" className="btn-secondary text-sm !py-2 !px-3 mt-4 inline-flex items-center gap-2">
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy credentials</>}
              </button>
            </div>
            <button onClick={close} data-testid="sa-add-firm-close" className="btn-primary w-full">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="ID firm / company name" testId="sa-firm-company" value={f.company_name} onChange={(v) => upd("company_name", v)} placeholder="Studio Nordic Pte Ltd" />
            <Field label="Admin's name" testId="sa-firm-admin-name" value={f.admin_name} onChange={(v) => upd("admin_name", v)} placeholder="Jane Tan" />
            <Field label="Admin's email" testId="sa-firm-admin-email" type="email" value={f.admin_email} onChange={(v) => upd("admin_email", v)} placeholder="jane@studionordic.com" />

            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Temporary password</label>
              <div className="flex gap-2">
                <input data-testid="sa-firm-admin-password" required type="text" value={f.admin_password}
                  onChange={(e) => upd("admin_password", e.target.value)} className="input-soft flex-1 font-mono"
                  placeholder="At least 6 characters" />
                <button type="button" onClick={genPw} data-testid="sa-firm-genpw"
                  className="btn-secondary !py-2 !px-3 inline-flex items-center gap-1 flex-shrink-0" title="Generate password">
                  <Dice5 size={16} /> Generate
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Package</label>
              <select
                data-testid="sa-firm-tier-select"
                required
                value={f.plan_tier}
                onChange={(e) => upd("plan_tier", e.target.value)}
                className="input-soft"
                disabled={tiersLoading}
              >
                {tiersLoading && <option value="">Loading tiers…</option>}
                {!tiersLoading && tiers.length === 0 && <option value="">No active tiers — create one first</option>}
                {!tiersLoading && tiers.map((t) => (
                  <option key={t.tier_id} value={t.slug} data-testid={`sa-firm-tier-option-${t.slug}`}>
                    {t.name}
                    {typeof t.price_monthly_cents === "number" ? ` — ${t.currency} ${(t.price_monthly_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo` : " — Contact sales"}
                  </option>
                ))}
              </select>
              {selectedTier && <TierPreview tier={selectedTier} />}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Field label="Subscription start" testId="sa-firm-start" type="date" value={f.subscription_start_date} onChange={(v) => upd("subscription_start_date", v)} />
              <Field label="Subscription end" testId="sa-firm-end" type="date" value={f.subscription_end_date} onChange={(v) => upd("subscription_end_date", v)} />
            </div>

            <button data-testid="sa-firm-submit" disabled={loading || !f.plan_tier} className="btn-primary w-full disabled:opacity-60">
              {loading ? "Creating…" : "Create ID firm"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, testId, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">{label}</label>
      <input data-testid={testId} type={type} required value={value}
        onChange={(e) => onChange(e.target.value)} className="input-soft" placeholder={placeholder} />
    </div>
  );
}
